import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Select from '@cloudscape-design/components/select';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import Input from '@cloudscape-design/components/input';
import { MarkerType } from 'reactflow';
import { getLineage } from '../api/lineage';
import { LineageCanvas } from '../components/lineage/LineageCanvas';
import { EventDetailPanel } from '../components/EventDetailPanel';
import { useLineageLayout } from '../components/lineage/useLineageLayout';

interface LineageResponse {
  lineage?: Array<{
    metadata: {
      eventId: string;
      eventType: string;
      version?: number;
      timestamp: string;
      causedBy?: string | string[] | null;
      source?: string;
      sourceStore?: string;
    };
    storeName?: string;
    _storeName?: string;
    data?: unknown;
  }>;
  depth?: number;
  truncated?: boolean;
}

const darkCanvasContainer: React.CSSProperties = {
  background: '#1a1a2e',
  borderRadius: '12px',
  padding: '2px',
  position: 'relative',
  backgroundImage: 'radial-gradient(circle, #2a2a4a 1px, transparent 1px)',
  backgroundSize: '24px 24px',
};

export function LineageGraph() {
  const { storeName, eventId } = useParams<{ storeName: string; eventId: string }>();
  const navigate = useNavigate();
  const [direction, setDirection] = useState('ancestors');
  const [maxDepth, setMaxDepth] = useState(50);
  const [depthInput, setDepthInput] = useState('50');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['lineage', storeName, eventId, direction, maxDepth],
    queryFn: () => getLineage(storeName!, eventId!, direction, maxDepth),
    enabled: !!storeName && !!eventId,
  });

  const responseData = data?.data as LineageResponse | undefined;
  const lineageEvents = responseData?.lineage || [];
  const { nodes, edges, storeColorMap } = useLineageLayout(lineageEvents, storeName);

  const styledEdges = edges.map(e => ({
    ...e,
    style: { stroke: '#4a6fa5', strokeWidth: 1.5 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#4a6fa5' },
  }));

  const selectedEvent = selectedNodeId
    ? lineageEvents.find((e) => e.metadata.eventId === selectedNodeId)
    : null;

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: 'Event stores', href: '/' },
          { text: storeName!, href: `/stores/${storeName}` },
          { text: 'Lineage', href: '#' },
        ]}
        onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
      />

      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Select
              selectedOption={{ value: direction, label: direction === 'ancestors' ? 'Ancestors' : 'Descendants' }}
              onChange={({ detail }) => setDirection(detail.selectedOption.value!)}
              options={[
                { value: 'ancestors', label: 'Ancestors' },
                { value: 'descendants', label: 'Descendants' },
              ]}
            />
            <div style={{ width: '80px' }}>
              <Input
                type="number"
                value={depthInput}
                onChange={({ detail }) => setDepthInput(detail.value)}
                onBlur={() => {
                  const v = Math.max(1, Math.min(100, Number(depthInput) || 1));
                  setDepthInput(String(v));
                  setMaxDepth(v);
                }}
                onKeyDown={({ detail }) => {
                  if (detail.key === 'Enter') {
                    const v = Math.max(1, Math.min(100, Number(depthInput) || 1));
                    setDepthInput(String(v));
                    setMaxDepth(v);
                  }
                }}
                ariaLabel="Max depth"
              />
            </div>
            <Button variant="link" onClick={() => navigate(`/stores/${storeName}`)}>
              ← Back to events
            </Button>
          </SpaceBetween>
        }
      >
        Lineage {lineageEvents.length > 0 ? `· ${lineageEvents.length} events · depth ${responseData?.depth || 0}` : ''}
        {responseData?.truncated && ' (truncated)'}
      </Header>

      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{ flex: selectedEvent ? '1 1 65%' : '1 1 100%' }}>
          {isLoading ? (
            <Box textAlign="center" padding="xxl"><span>Loading lineage...</span></Box>
          ) : nodes.length === 0 ? (
            <Box textAlign="center" padding="xxl"><span>No lineage found for this event.</span></Box>
          ) : (
            <div style={darkCanvasContainer}>
              <LineageCanvas
                nodes={nodes}
                edges={styledEdges}
                onNodeClick={setSelectedNodeId}
                selectedNodeId={selectedNodeId}
              />
              {storeColorMap.size > 1 && (
                <div style={{ display: 'flex', gap: '12px', padding: '8px 12px', flexWrap: 'wrap' }}>
                  {Array.from(storeColorMap.entries()).map(([store, color]) => (
                    <div key={store} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: color, display: 'inline-block' }} />
                      <span style={{ color: '#c9d1d9', fontSize: '11px' }}>{store}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {selectedEvent && (
          <div style={{ flex: '0 0 35%', maxHeight: '650px', overflow: 'auto' }}>
            <EventDetailPanel
              event={selectedEvent as unknown as Record<string, unknown>}
              storeName={storeName!}
              onClose={() => setSelectedNodeId(null)}
              onSelectCausedBy={(id) => setSelectedNodeId(id)}
            />
          </div>
        )}
      </div>
    </SpaceBetween>
  );
}
